// src/pages/candidate/Profile/Resume.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { toast } from 'react-toastify';
import { 
  getResumes, 
  uploadResume, 
  deleteResume,
  setPrimaryResume 
} from '../../../services/candidate/resumeService';
import ConfirmModal from '../../../components/ui/ConfirmModal';

const Resume = ({ profileData, onUpdate }) => {
  const [resumes, setResumes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [settingPrimaryId, setSettingPrimaryId] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [resumeToDelete, setResumeToDelete] = useState(null);
  
  const fileInputRef = useRef(null);

  // Fetch resumes on component mount
  useEffect(() => {
    fetchResumesData();
  }, []);

  const fetchResumesData = async () => {
    try {
      setLoading(true);
      const response = await getResumes();
      
      if (response.success) {
        setResumes(response.data || []);
      } else {
        toast.error('Failed to load resumes');
      }
    } catch (error) {
      console.error('Error fetching resumes:', error);
      toast.error('Failed to load resumes');
    } finally {
      setLoading(false);
    }
  };

  // Handle drag events for modal
  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      validateAndUploadFile(file);
    }
  }, []);

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      validateAndUploadFile(file);
    }
  };

  const validateAndUploadFile = async (file) => {
    if (!file) return;

    // Validate file type
    const allowedTypes = [
      'application/pdf', 
      'application/msword', 
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'image/jpeg',
      'image/jpg',
      'image/png'
    ];
    
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload a PDF, Word document, or Image (JPG, JPEG, PNG)');
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }

    // Validate file extension
    const allowedExtensions = ['.pdf', '.doc', '.docx', '.jpg', '.jpeg', '.png'];
    const fileExtension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
    if (!allowedExtensions.includes(fileExtension)) {
      toast.error('Invalid file extension. Allowed: PDF, DOC, DOCX, JPG, JPEG, PNG');
      return;
    }

    setUploading(true);

    try {
      const response = await uploadResume(file);

      if (response.success) {
        toast.success(response.message || 'Resume uploaded successfully!');
        
        // Refresh the resumes list
        fetchResumesData();
        
        // Close modal
        setIsModalOpen(false);
        
        // Trigger parent update for profile completion
        if (onUpdate) {
          onUpdate();
        }
      } else {
        toast.error(response.message || 'Failed to upload resume');
      }
    } catch (error) {
      console.error('Error uploading resume:', error);
      toast.error(error.response?.data?.message || 'Failed to upload resume. Please try again.');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDeleteResume = async (id) => {
    setResumeToDelete(id);
    setShowConfirmModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!resumeToDelete) return;

    setDeletingId(resumeToDelete);
    try {
      const response = await deleteResume(resumeToDelete);
      
      if (response.success) {
        toast.success(response.message || 'Resume deleted successfully!');
        
        // Update local state
        setResumes(prev => prev.filter(resume => resume._id !== resumeToDelete));
        
        // Trigger parent update for profile completion
        if (onUpdate) {
          onUpdate();
        }
      } else {
        toast.error(response.message || 'Failed to delete resume');
      }
    } catch (error) {
      console.error('Error deleting resume:', error);
      toast.error('Failed to delete resume. Please try again.');
    } finally {
      setDeletingId(null);
      setResumeToDelete(null);
      setShowConfirmModal(false);
    }
  };

  const handleSetPrimary = async (id) => {
    setSettingPrimaryId(id);
    try {
      const response = await setPrimaryResume(id);
      
      if (response.success) {
        toast.success(response.message || 'Primary resume updated successfully!');
        
        // Update local state
        setResumes(prev => prev.map(resume => ({
          ...resume,
          isPrimary: resume._id === id
        })));
      } else {
        toast.error(response.message || 'Failed to update primary resume');
      }
    } catch (error) {
      console.error('Error setting primary resume:', error);
      //toast.error('Failed to update primary resume. Please try again.');
    } finally {
      setSettingPrimaryId(null);
    }
  };

  const handleDownload = (filePath, fileName) => {
    const link = document.createElement('a');
    link.href = filePath;
    link.download = fileName;    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleView = (filePath) => {
    window.open(filePath, '_blank');
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getFileIcon = (fileType) => {
    if (fileType.includes('pdf')) return 'fas fa-file-pdf text-red-500';
    if (fileType.includes('word') || fileType.includes('document')) return 'fas fa-file-word text-blue-500';
    if (fileType.includes('image')) return 'fas fa-file-image text-green-500';
    return 'fas fa-file text-gray-500';
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="resume">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-800">Uploaded Resume(s)</h3>
        </div>
        <button
          onClick={() => setIsModalOpen(true)}
          className="flex items-center gap-2 btn-primary text-white px-4 py-2 rounded-lg transition-colors"
        >
          <i className="fas fa-plus"></i>
          Upload Resume
        </button>
      </div>

      {/* Resumes List */}
      {resumes.length === 0 ? (
        <div className="bg-gray-50 rounded-xl p-10 text-center">
          <div className="mb-4">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-200 rounded-full mb-4">
              <i className="fas fa-file text-2xl text-gray-400"></i>
            </div>
            <h4 className="text-lg font-semibold text-gray-800 mb-2">No Resumes Uploaded</h4>
            <p className="text-gray-600 max-w-md mx-auto">
              You haven't uploaded any resumes yet. Click "Upload Resume" to add your first resume.
            </p>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {resumes.map((resume) => (
            <div 
              key={resume._id}
              className={`bg-white border rounded-xl p-6 ${resume.isPrimary ? 'border-primary-500' : 'border-gray-200'}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-4">
                  <div className={`p-3 rounded-lg ${resume.isPrimary ? 'bg-primary-100' : 'bg-gray-100'}`}>
                    <i className={`${getFileIcon(resume.fileType)} text-2xl`}></i>
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="text-lg font-semibold text-gray-800">{resume.originalName}</h4>
                      {resume.isPrimary && (
                        <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800">
                          Primary
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-gray-600 space-y-1">
                      <p>Uploaded on {formatDate(resume.uploadDate)}</p>
                      {/* <p>Size: {formatFileSize(resume.fileSize)} • Type: {resume.fileType.split('/')[1].toUpperCase()}</p> */}
                    </div>
                  </div>
                </div>
                
                <div className="flex gap-2">
                  {/* <button
                    onClick={() => handleView(resume.filePath)}
                    className="flex items-center gap-2 bg-gray-100 text-gray-700 px-3 py-2 rounded-lg hover:bg-gray-200 transition-colors"
                    title="View resume"
                  >
                    <i className="fas fa-eye"></i>
                  </button> */}
                  <button
                    onClick={() => handleDownload(resume.filePath, resume.originalName)}
                    className="flex items-center gap-2 bg-green-100 text-green-700 px-3 py-2 rounded-lg hover:bg-green-200 transition-colors"
                    title="Download resume"
                  >
                    <i className="fas fa-download"></i>
                  </button>
                  {!resume.isPrimary && (
                    <button
                      onClick={() => handleSetPrimary(resume._id)}
                      disabled={settingPrimaryId === resume._id}
                      className="flex items-center gap-2 bg-blue-100 text-blue-700 px-3 py-2 rounded-lg hover:bg-blue-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Set as primary"
                    >
                      {settingPrimaryId === resume._id ? (
                        <i className="fas fa-spinner fa-spin"></i>
                      ) : (
                        <i className="fas fa-star"></i>
                      )}
                    </button>
                  )}
                  <button
                    onClick={() => handleDeleteResume(resume._id)}
                    disabled={deletingId === resume._id}
                    className="flex items-center gap-2 bg-red-100 text-red-700 px-3 py-2 rounded-lg hover:bg-red-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Delete resume"
                  >
                    {deletingId === resume._id ? (
                      <i className="fas fa-spinner fa-spin"></i>
                    ) : (
                      <i className="fas fa-trash"></i>
                    )}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Tips Section */}      
      <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-gray-50 p-4 rounded-lg">
          <h5 className="font-semibold text-gray-800 mb-2">
            <i className="fas fa-check text-green-500 mr-2"></i>
            Keep it Updated
          </h5>
          <p className="text-sm text-gray-600">
            Regularly update your resume with new skills and experiences
          </p>
        </div>
        <div className="bg-gray-50 p-4 rounded-lg">
          <h5 className="font-semibold text-gray-800 mb-2">
            <i className="fas fa-check text-green-500 mr-2"></i>
            Be Specific
          </h5>
          <p className="text-sm text-gray-600">
            Include clear achievements and concrete details to strengthen your profile
          </p>
        </div>
        <div className="bg-gray-50 p-4 rounded-lg">
          <h5 className="font-semibold text-gray-800 mb-2">
            <i className="fas fa-check text-green-500 mr-2"></i>
            Resume Consistency
          </h5>
          <p className="text-sm text-gray-600">
            Ensure your resume aligns with the information on your profile
          </p>
        </div>
      </div>

      {/* Upload Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-lg font-semibold">Upload Resume</h3>
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="text-gray-400 hover:text-gray-600"
                  disabled={uploading}
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>

              <div 
                className={`relative border-2 ${dragActive ? 'border-primary-500 bg-primary-50' : 'border-dashed border-gray-300'} rounded-lg p-8 transition-all duration-200 ${uploading ? 'opacity-75 pointer-events-none' : ''}`}
                onDragEnter={handleDrag}
                onDragOver={handleDrag}
                onDragLeave={handleDrag}
                onDrop={handleDrop}
                onClick={() => !uploading && fileInputRef.current?.click()}
              >
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileSelect}
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                  className="hidden"
                />

                <div className="flex flex-col items-center justify-center text-center">
                  <div className={`mb-4 p-4 rounded-full ${dragActive ? 'bg-primary-100' : 'bg-gray-100'}`}>
                    <i className={`fas fa-cloud-upload-alt text-3xl ${dragActive ? 'text-primary-600' : 'text-gray-400'}`}></i>
                  </div>
                  
                  <div className="mb-4">
                    <h4 className="text-lg font-semibold text-gray-800 mb-2">
                      {dragActive ? 'Drop your file here' : 'Drag & drop or click to upload'}
                    </h4>
                    <p className="text-gray-600 mb-1">
                      Upload your resume (PDF, DOC, DOCX, JPG, PNG)
                    </p>
                    <p className="text-sm text-gray-500">
                      Maximum file size: 5MB
                    </p>
                  </div>

                  <button 
                    className={`px-6 py-2 rounded-lg font-medium transition-colors ${uploading ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'btn-primary text-white'}`}
                    disabled={uploading}
                    onClick={(e) => {
                      e.stopPropagation();
                      fileInputRef.current?.click();
                    }}
                  >
                    {uploading ? (
                      <>
                        <i className="fas fa-spinner fa-spin mr-2"></i>
                        Uploading...
                      </>
                    ) : 'Browse File'}
                  </button>
                </div>

                {dragActive && (
                  <div className="absolute inset-0 bg-primary-50 bg-opacity-50 rounded-lg flex items-center justify-center">
                    <div className="text-center">
                      <i className="fas fa-cloud-upload-alt text-4xl text-primary-600 mb-3"></i>
                      <p className="text-primary-700 font-medium">Drop your file here</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Supported formats */}
              <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                <h4 className="font-medium text-gray-700 mb-2">Supported Formats:</h4>
                <div className="flex flex-wrap gap-2">
                  <span className="px-3 py-1 bg-red-100 text-red-800 rounded-full text-sm">PDF</span>
                  <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">DOC</span>
                  <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">DOCX</span>
                  <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm">JPG</span>
                  <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm">JPEG</span>
                  <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm">PNG</span>
                </div>
              </div>

              {/* Form Actions */}
              <div className="flex gap-3 justify-end pt-6 border-t border-gray-200 mt-6">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                  disabled={uploading}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      <ConfirmModal
        isOpen={showConfirmModal}
        onClose={() => {
          setShowConfirmModal(false);
          setResumeToDelete(null);
        }}
        onConfirm={handleConfirmDelete}
        title="Delete Resume"
        message="Are you sure you want to delete this resume? This action cannot be undone."
        confirmText="Delete"
        confirmColor="btn-primary"
      />
    </div>
  );
};

export default Resume;